import React, { useEffect, useRef, useState } from "react";
import reportHealthService from "../../../../../../../../../Service/OPSServices/StoreHealthServices/reportHealthService.js";
import PayloadView, { size } from "./PayloadView.js";

const STATE = { saved: ["ok", "saved"], error: ["red", "error"], waiting: ["amber", "pending / completed"] };
const utc = (iso) =>
  iso
    ? `${new Date(iso).toLocaleString("en-GB", {
        timeZone: "UTC",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })} UTC`
    : "–";

function pretty(text) {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch (e) {
    return text;
  }
}

/** Every log row for one store + report on the day, and the payload rows of its jobs. */
export default function AttemptsPanel({
  date,
  store,
  column,
  cell,
  onClose,
  onRetry,
  retrying,
  retryAllowed,
  retryMaxAgeDays,
}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [shown, setShown] = useState(null); // payload row id being shown
  const closeRef = useRef(null);

  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    reportHealthService
      .getAttempts(date, store.profileId, column.name)
      .then((d) => {
        if (!live) return;
        setData(d);
        // a saved report opens its latest payload straight away
        if (cell.state === "saved" && d.payloads && d.payloads.length) {
          setShown(d.payloads[d.payloads.length - 1].id);
        }
      })
      .catch((e) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [date, store.profileId, column.name, cell.state]);

  useEffect(() => {
    closeRef.current && closeRef.current.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="rh-overlay" onClick={onClose}>
      <aside
        className="rh-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rh-panel-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="rh-panel-head">
          <div>
            <h2 id="rh-panel-title">{column.name}</h2>
            <div className="ams-panel-note">
              {store.storeName || "Unknown store"} · profile {store.profileId} · {date} (UTC)
            </div>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="ams-banner-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className={`rh-verdict rh-verdict--${cell.level}`}>
          {cell.text || "No log rows on this day."}
        </div>
        <div className="rh-actions">
          {cell.state === "missing" || cell.state === "not_yet" ? (
            <span className="ams-hint">
              There is no log row to retry: the scheduled job requests this report.
            </span>
          ) : !retryAllowed ? (
            <span className="ams-hint">
              Reports older than {retryMaxAgeDays} days cannot be regenerated by Amazon.
            </span>
          ) : (
            <>
              <button
                type="button"
                className={`ams-btn${cell.retryable ? " ams-btn--primary" : ""}`}
                disabled={retrying}
                onClick={() => {
                  if (
                    !cell.retryable &&
                    !window.confirm(
                      cell.state === "saved"
                        ? "This report is already saved. Regenerate it anyway?"
                        : "Amazon is still preparing this report. Regenerate it anyway?"
                    )
                  )
                    return;
                  onRetry();
                }}
              >
                {retrying ? (
                  <>
                    <span className="ams-spin" aria-hidden="true" />
                    Retrying…
                  </>
                ) : cell.retryable ? (
                  "Retry this report"
                ) : (
                  "Retry anyway"
                )}
              </button>
              <span className="ams-hint">
                Regenerates it with the same job, like RetryErrorReportsV3. It shows as Waiting until
                the next save run (every 2 h).
              </span>
            </>
          )}
        </div>

        {error && <div className="ams-banner ams-banner--err">Could not load the log: {error}</div>}
        {!data && !error && <div className="ams-empty">Loading log rows…</div>}
        {data && (
          <>
            <h3 className="rh-h3">Saved data</h3>
            {data.payloads && data.payloads.length ? (
              <ul className="rh-payloads">
                {data.payloads.map((p) => (
                  <li key={p.id}>
                    <div className="rh-payload-line">
                      <span>
                        job {p.jobId}
                        {p.savedAt ? ` · saved ${utc(p.savedAt)}` : ""} · {size(p.bytes)}
                      </span>
                      <button
                        type="button"
                        className="ams-link"
                        onClick={() => setShown(shown === p.id ? null : p.id)}
                      >
                        {shown === p.id ? "Hide data" : "Show data"}
                      </button>
                    </div>
                    {shown === p.id && (
                      <PayloadView
                        payloadId={p.id}
                        filename={`${column.name
                          .replace(/[^A-Za-z0-9]+/g, "-")
                          .toLowerCase()}-${store.profileId}-job${p.jobId}.json`}
                      />
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="ams-hint">No saved data for these jobs in the reports table.</p>
            )}
            <h3 className="rh-h3">Log rows ({data.attempts.length})</h3>
            {data.attempts.length === 0 && (
              <p className="ams-hint">Nothing was requested for this report on this day.</p>
            )}
            <ul className="rh-attempts">
              {data.attempts.map((a) => (
                <li key={a.id}>
                  <div className="rh-attempt-head">
                    <span className={`ams-tag ams-tag--${a.level}`}>{a.state}</span>
                    <span>job {a.jobId}</span>
                    <span className="ams-asof">updated {utc(a.updatedAt)}</span>
                  </div>
                  <pre className="rh-log">{pretty(a.message)}</pre>
                </li>
              ))}
            </ul>
          </>
        )}
      </aside>
    </div>
  );
}
